// ── 연차 관리 유틸리티 함수 ────────────────────────────────────────────────────
import { dayOfWeekUTC, isValidYmd, addMonthsToYmd } from '../../../utils/date';
import { BULK_COLUMNS, LEAVE_TYPES_BULK, LEAVE_CATEGORIES, MOBILE_BREAKPOINT } from './constants';

// ── 폼 초기값 생성 ─────────────────────────────────────────────────────────────
export function emptyForm(userId = '') {
  return {
    user_id: userId,
    date: '',
    date_to: '',
    type: '연차',
    category: '유급',
    count: '1',
    reason: '',
    event_type_id: '',
    event_name: '',
    payroll_effect: 'paid_cover',
    leave_balance_effect: 'deduct',
    monthly_grant_effect: 'neutral',
    calendar_effect: 'full_day',
    grant_decision_source: 'manual',
    affects_weekly_holiday: true, // 무급일 때만 폼에서 false 선택 가능(회사 지정 무급휴가 = 주휴 유지)
  };
}

export function inferEffectDefaults(type, category) {
  const t = String(type || '').trim();
  const c = String(category || '').trim();
  if (t === '생성' || t === '자동생성' || t === '포상' || c === '생성' || c === '포상') {
    return {
      payroll_effect: 'none',
      leave_balance_effect: 'grant',
      monthly_grant_effect: 'neutral',
      calendar_effect: 'none',
      grant_decision_source: 'auto',
    };
  }
  if (t === '정산' || c === '정산') {
    return {
      payroll_effect: 'none',
      leave_balance_effect: 'settlement',
      monthly_grant_effect: 'neutral',
      calendar_effect: 'none',
      grant_decision_source: 'manual',
    };
  }
  if (c === '무급') {
    return {
      payroll_effect: 'unpaid_absence',
      leave_balance_effect: 'none',
      monthly_grant_effect: 'blocks',
      calendar_effect: t === '반차(전)' ? 'half_am' : t === '반차(후)' || t === '반차' ? 'half_pm' : 'full_day',
      grant_decision_source: 'manual',
    };
  }
  if (c === '유급') {
    return {
      payroll_effect: 'paid_cover',
      leave_balance_effect: t === '경조사' ? 'none' : 'deduct',
      monthly_grant_effect: 'neutral',
      calendar_effect: t === '반차(전)' ? 'half_am' : t === '반차(후)' || t === '반차' ? 'half_pm' : 'full_day',
      grant_decision_source: 'manual',
    };
  }
  return {
    payroll_effect: 'none',
    leave_balance_effect: 'none',
    monthly_grant_effect: 'neutral',
    calendar_effect: 'none',
    grant_decision_source: 'manual',
  };
}

// ── 대량 입력 행 초기값 ────────────────────────────────────────────────────────
export function createEmptyBulkRow() {
  return { date: '', name: '', type: '', category: '', count: '', reason: '' };
}

// ── 대량 행이 완전히 비어있는지 판별 ──────────────────────────────────────────
export function isBulkRowEmpty(row) {
  return BULK_COLUMNS.every((col) => String(row[col.key] || '').trim() === '');
}

// ── 날짜 문자열 정규화 (점/슬래시 → 하이픈) ──────────────────────────────────
export function normalizeDateInput(value) {
  return String(value || '').trim().replace(/\./g, '-').replace(/\//g, '-');
}

// ── YYYY-MM-DD 유효성 검사 ─────────────────────────────────────────────────────
// isValidYmd: utils/date.js 사용 (new Date 직접 사용 금지)
export function isValidDateText(text) {
  return isValidYmd(text);
}

// ── 날짜 문자열에 개월 수 더하기 ──────────────────────────────────────────────
// addMonthsToYmd: utils/date.js 사용 (new Date/toISOString 직접 사용 금지)
export function addMonthsToDateText(ymd, months) {
  return addMonthsToYmd(ymd, months);
}

// ── 날짜 a가 b보다 이전인지 비교 ──────────────────────────────────────────────
export function isBeforeYmd(a, b) {
  if (!a || !b) return false;
  return String(a) < String(b);
}

// ── 대량 입력 셀 값 정규화 ────────────────────────────────────────────────────
export function normalizeBulkCellValue(key, rawValue) {
  const value = String(rawValue || '').trim();
  if (!value) return '';
  if (key === 'date') return normalizeDateInput(value);
  if (key === 'count') return value.replace(/[^\d.]/g, '');
  return value;
}

// ── 클립보드 텍스트를 2차원 배열로 파싱 (탭 구분) ────────────────────────────
export function parseClipboardMatrix(rawText) {
  return String(rawText || '')
    .replace(/\r\n/g, '\n')
    .split('\n')
    .filter((line) => line.length > 0)
    .map((line) => line.split('\t'));
}

// ── 대량 입력 셀 오류 메시지 반환 ─────────────────────────────────────────────
export function getBulkCellError(row, key, userMap) {
  const value = String(row[key] || '').trim();
  if (key === 'date') {
    if (!value) return '연차사용일은 필수입니다.';
    if (!isValidDateText(value)) return '날짜는 YYYY-MM-DD 형식이어야 합니다.';
    return '';
  }
  if (key === 'name') {
    if (!value) return '성명은 필수입니다.';
    if (!userMap.has(value)) return '직원명 불일치';
    return '';
  }
  if (key === 'type') {
    if (!value) return '종류는 필수입니다.';
    if (!LEAVE_TYPES_BULK.includes(value)) return '종류 값이 올바르지 않습니다.';
    return '';
  }
  if (key === 'category') {
    if (!value) return '구분은 필수입니다.';
    if (!LEAVE_CATEGORIES.includes(value)) return '구분 값이 올바르지 않습니다.';
    return '';
  }
  if (key === 'count') {
    if (!value) return '개수는 필수입니다.';
    const n = Number(value);
    if (Number.isNaN(n) || n <= 0) return '개수는 0보다 큰 숫자여야 합니다.';
    return '';
  }
  return '';
}

// ── 자동생성 행 여부 판별 ─────────────────────────────────────────────────────
// 기존 자동생성('자동생성')과 신규 표기('생성')를 모두 자동생성 행으로 판별합니다.
export function isAutoGeneratedRow(row) {
  return (
    row.type === '생성' || row.type === '자동생성' ||
    row.category === '생성' ||
    String(row.reason || '').includes('입사일 기준 자동생성')
  );
}

// ── 모바일 여부 판별 ──────────────────────────────────────────────────────────
export function getIsMobile() {
  return typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT;
}

// ── 잔여 연차 셀 색상 (양수: 초록, 0: 회색, 음수: 빨강) — rgba 15% 다크모드 대응 ──
export function remainingCellStyle(remaining) {
  const value = Number(remaining || 0);
  const base = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    minWidth: 40, fontWeight: 800, borderRadius: 999, padding: '1px 8px',
    border: '1px solid transparent', fontSize: 11,
  };
  if (value < 0)   return { ...base, color: '#991b1b',          background: 'rgba(239,68,68,0.15)',  borderColor: 'rgba(239,68,68,0.3)' };
  if (value === 0) return { ...base, color: 'var(--text-sub)',  background: 'var(--bg-subtle)',      borderColor: 'var(--border)' };
  return             { ...base, color: '#166534',          background: 'rgba(34,197,94,0.15)',  borderColor: 'rgba(34,197,94,0.3)' };
}

// ── 회사 뱃지 스타일 (통합 모드에서 외부 회사 표시용) — rgba 15% 다크모드 대응 ──
const COMPANY_BADGE_COLORS = {
  PINE: { bg: 'rgba(29,78,216,0.15)',  color: '#1d4ed8' },
  DOT:  { bg: 'rgba(234,88,12,0.15)',  color: '#c2410c' },
  LNJ:  { bg: 'rgba(21,128,61,0.15)',  color: '#15803d' },
};
export function companyBadgeStyle(name) {
  const c = COMPANY_BADGE_COLORS[name] || { bg: 'var(--bg-subtle)', color: 'var(--text-sub)' };
  return {
    display: 'inline-block',
    fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999,
    background: c.bg, color: c.color,
  };
}

// ── 연차 요약 타입 필터 정의 (직원 페이지 useLeaveCalcs와 동일 로직) ──────────
export const LEAVE_FILTER_DEFS = [
  { key: 'generated', label: '생성',   color: '#1d4ed8' },
  { key: 'used',      label: '사용',   color: '#0f766e' },
  { key: 'paid_recognized', label: '유급인정', color: '#2563eb' },
  { key: 'unpaid',    label: '무급',   color: '#9a3412' },
  { key: 'settled',   label: '정산',   color: '#7c3aed' },
  { key: 'family',    label: '경조사', color: '#be185d' },
  { key: 'bonus',     label: '포상',   color: '#065f46' },
  { key: 'corrected', label: '정정',   color: 'var(--text-muted)' },
];

// ── 연차 행이 특정 필터 키와 일치하는지 판정 (dashboard/utils.js matchesLeaveFilter와 동일) ──
export function matchesLeaveFilter(row, filterKey) {
  const type = String(row?.type || '').trim();
  const category = String(row?.category || '').trim();
  const balanceEffect = String(row?.leave_balance_effect || 'legacy');
  const payrollEffect = String(row?.payroll_effect || 'legacy');
  if (filterKey === 'generated') return balanceEffect === 'grant' || (balanceEffect === 'legacy' && (type === '생성' || type === '자동생성'));
  if (filterKey === 'used')      return balanceEffect === 'deduct' || (balanceEffect === 'legacy' && (type === '연차' || type === '반차' || type === '반차(전)' || type === '반차(후)') && category === '유급');
  if (filterKey === 'paid_recognized') return payrollEffect === 'paid_cover' && balanceEffect === 'none';
  if (filterKey === 'unpaid')    return payrollEffect === 'unpaid_absence' || (payrollEffect === 'legacy' && category === '무급');
  if (filterKey === 'settled')   return balanceEffect === 'settlement' || (balanceEffect === 'legacy' && (type === '정산' || category === '정산'));
  if (filterKey === 'family')    return type === '경조사' || category === '경조사';
  if (filterKey === 'bonus')     return type === '포상' || category === '포상';
  if (filterKey === 'corrected') return Boolean(row?.is_correction) || type === '정정' || category === '정정';
  return true;
}

// ── 뱃지 스타일 생성 헬퍼 ─────────────────────────────────────────────────────
export function badgeStyle(colorMap, key) {
  const c = colorMap[key] || { bg: 'var(--bg-subtle)', color: 'var(--text-sub)' };
  return {
    display: 'inline-block',
    padding: '2px 9px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    background: c.bg,
    color: c.color,
    whiteSpace: 'nowrap',
  };
}

// ── 요일 키 배열 ──────────────────────────────────────────────────────────────
export const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

// ── 'YYYY-MM-DD' → 요일 키 (mon/tue/…/sun) ───────────────────────────────────
// dayOfWeekUTC: utils/date.js 사용 (new Date 직접 사용 금지)
export function getWeekdayKeyFromYmd(ymd) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(ymd || ''))) return null;
  return WEEKDAY_KEYS[dayOfWeekUTC(ymd)];
}

/**
 * 특정 직원·날짜의 반차기준시간 조회
 * @param {object[]} templates       - attendance settings templates 배열
 * @param {string}   attendanceType  - 직원의 attendance_time_type
 * @param {string}   ymd             - 'YYYY-MM-DD'
 * @returns {string|null} HH:mm 또는 null (미설정)
 */
export function getHalfLeaveThreshold(templates, attendanceType, ymd) {
  const key = String(attendanceType || '').trim().toLowerCase();
  const template = (templates || []).find(
    (t) => String(t.name || '').trim().toLowerCase() === key
      || String(t.id || '').trim().toLowerCase() === key
  ) || (templates || [])[0];
  const weekdayKey = getWeekdayKeyFromYmd(ymd);
  return template?.weekly_rules?.[weekdayKey]?.half_leave_threshold ?? null;
}

export function getAttendanceSettingsForUser(settingsByCompany, fallbackSettings, user) {
  const companyCode = String(user?.company_code || 'PINE').trim().toUpperCase() || 'PINE';
  return settingsByCompany?.[companyCode] || fallbackSettings || null;
}
